import React, { useState, useEffect, useCallback } from 'react';
import { Scale, ArrowDown, ArrowUp, RefreshCw, AlertTriangle, CheckCircle, ChevronRight, Plus, X } from 'lucide-react';
import { API_URL } from '../../services/api';

const TYPE_LABELS = {
  RECEPTION_STOCK: { label: 'Réception stock → Chef', color: 'blue', dir: 'in' },
  SUPPLEMENT: { label: 'Supplément stock → Chef', color: 'orange', dir: 'in' },
  DISTRIBUTION_OUVRIER: { label: 'Chef → Ouvrier', color: 'purple', dir: 'out' },
  RETOUR_BIJOU: { label: 'Ouvrier → Bijou fini', color: 'green', dir: 'out' },
  RETOUR_LIMAILLES_OUVRIER: { label: 'Ouvrier → Limailles', color: 'gray', dir: 'out' },
  RETOUR_LIMAILLES_CHEF: { label: 'Chef → Limailles stock', color: 'gray', dir: 'out' },
  RETOUR_STOCK: { label: 'Chef → Retour stock', color: 'teal', dir: 'out' },
};

export default function TracabiliteOr() {
  const [ofs, setOfs] = useState([]);
  const [selectedOf, setSelectedOf] = useState(null);
  const [bilan, setBilan] = useState(null);
  const [ouvriers, setOuvriers] = useState([]);
  const [alertes, setAlertes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    type: 'RECEPTION_STOCK', poids_grammes: '', ouvrier_id: '', motif: ''
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  // Get current user from localStorage
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  const loadOfs = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/tracabilite-or/of-actifs`);
      const d = await r.json();
      setOfs(Array.isArray(d) ? d : []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  const loadAlertes = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/tracabilite-or/alertes`);
      const d = await r.json();
      setAlertes(Array.isArray(d) ? d : []);
    } catch (e) {}
  }, []);

  const loadOuvriers = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/tracabilite-or/ouvriers`);
      const d = await r.json();
      setOuvriers(Array.isArray(d) ? d : []);
    } catch (e) {}
  }, []);

  useEffect(() => {
    loadOfs();
    loadAlertes();
    loadOuvriers();
  }, [loadOfs, loadAlertes, loadOuvriers]);

  const selectOf = async (of) => {
    setSelectedOf(of);
    setBilan(null);
    setShowForm(false);
    try {
      const r = await fetch(`${API_URL}/tracabilite-or/bilan/of/${of.id}`);
      const d = await r.json();
      setBilan(d);
    } catch (e) {}
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.poids_grammes || parseFloat(form.poids_grammes) <= 0) {
      setMsg({ type: 'error', text: 'Poids invalide' }); return;
    }
    if (form.type === 'SUPPLEMENT' && !form.motif) {
      setMsg({ type: 'error', text: 'Justification obligatoire pour un supplément' }); return;
    }
    if (form.type === 'DISTRIBUTION_OUVRIER' && !form.ouvrier_id) {
      setMsg({ type: 'error', text: 'Sélectionnez un ouvrier' }); return;
    }
    setSaving(true);
    try {
      const body = {
        of_id: selectedOf.id,
        type: form.type,
        poids_grammes: parseFloat(form.poids_grammes),
        ouvrier_id: form.ouvrier_id || null,
        motif: form.motif || null,
        chef_id: currentUser.id,
      };
      const r = await fetch(`${API_URL}/tracabilite-or/transferts`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const d = await r.json();
      if (!r.ok) { setMsg({ type: 'error', text: d.error }); setSaving(false); return; }
      setMsg({ type: 'ok', text: `Mouvement enregistré — solde chef: ${d.solde_chef}g` });
      setForm({ type: 'RECEPTION_STOCK', poids_grammes: '', ouvrier_id: '', motif: '' });
      setShowForm(false);
      // Refresh bilan
      const rb = await fetch(`${API_URL}/tracabilite-or/bilan/of/${selectedOf.id}`);
      setBilan(await rb.json());
      loadOfs();
      loadAlertes();
    } catch (e) { setMsg({ type: 'error', text: 'Erreur réseau' }); }
    setSaving(false);
  };

  const needsOuvrier = form.type === 'DISTRIBUTION_OUVRIER' || form.type === 'RETOUR_BIJOU' || form.type === 'RETOUR_LIMAILLES_OUVRIER';
  const needsMotif = form.type === 'SUPPLEMENT';

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Scale className="h-5 w-5 text-yellow-600" />
            Tracabilité Or
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">Suivi gramme par gramme — Stock → Chef → Ouvrier → Bijou</p>
        </div>
        <button onClick={() => { loadOfs(); loadAlertes(); }}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs bg-gray-100 text-gray-600 hover:bg-gray-200">
          <RefreshCw className="h-3 w-3" /> Actualiser
        </button>
      </div>

      {/* Alertes */}
      {alertes.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <span className="text-sm font-semibold text-red-700">{alertes.length} OF(s) avec or non justifié</span>
          </div>
          <div className="space-y-1">
            {alertes.map(a => (
              <div key={`${a.of_id}-${a.chef_id}`}
                className="flex items-center justify-between text-xs bg-white rounded px-3 py-2 border border-red-100 cursor-pointer hover:bg-red-50"
                onClick={() => { const of = ofs.find(o => o.id === a.of_id); if (of) selectOf(of); }}>
                <span className="font-mono text-gray-700">{a.numero_ordre}</span>
                <span className="text-gray-500">{a.chef_nom}</span>
                <span className="font-bold text-red-600">+{a.solde_chef}g non justifié</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* OF List */}
        <div className="lg:col-span-1 bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Ordres de fabrication</span>
          </div>
          {loading ? (
            <div className="p-4 text-center text-xs text-gray-400">Chargement...</div>
          ) : ofs.length === 0 ? (
            <div className="p-4 text-center text-xs text-gray-400">Aucun OF en cours</div>
          ) : (
            <div className="divide-y divide-gray-50 max-h-96 overflow-y-auto">
              {ofs.map(of => {
                const solde = parseFloat(of.solde_chef || 0);
                const hasAlert = solde > 0.05;
                const isSelected = selectedOf?.id === of.id;
                return (
                  <button key={of.id} onClick={() => selectOf(of)}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center justify-between gap-2 ${isSelected ? 'bg-yellow-50 border-l-2 border-yellow-400' : ''}`}>
                    <div className="min-w-0">
                      <div className="text-xs font-mono font-semibold text-gray-800 truncate">{of.numero_ordre}</div>
                      <div className="text-xs text-gray-500 truncate">{of.ref_article || of.description}</div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`text-xs px-1.5 py-0.5 rounded ${of.statut === 'en_cours' ? 'bg-blue-100 text-blue-700' : of.statut === 'termine' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {of.statut}
                        </span>
                        {of.poids_metal && <span className="text-xs text-gray-400">{of.poids_metal}g prévu</span>}
                      </div>
                    </div>
                    <div className="flex-shrink-0 flex items-center gap-1">
                      {hasAlert ? (
                        <span className="text-xs font-bold text-red-600 flex items-center gap-0.5">
                          <AlertTriangle className="h-3 w-3" />{solde}g
                        </span>
                      ) : of.nb_transferts > 0 ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : null}
                      <ChevronRight className="h-4 w-4 text-gray-300" />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Bilan */}
        <div className="lg:col-span-2 space-y-3">
          {!selectedOf ? (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400 text-sm">
              Sélectionnez un OF pour voir le bilan or
            </div>
          ) : (
            <>
              {/* OF Header */}
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h2 className="font-bold text-gray-900 font-mono">{selectedOf.numero_ordre}</h2>
                    <p className="text-xs text-gray-500">{selectedOf.ref_article} — {selectedOf.metal} {selectedOf.couleur_or}</p>
                  </div>
                  <button onClick={() => setShowForm(!showForm)}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs text-white"
                    style={{ background: showForm ? '#6b7280' : '#d4af37' }}>
                    {showForm ? <><X className="h-3 w-3" />Annuler</> : <><Plus className="h-3 w-3" />Mouvement</>}
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="bg-blue-50 rounded-lg p-2">
                    <div className="text-xs text-blue-600 font-medium">Prévu</div>
                    <div className="text-lg font-bold text-blue-800">{selectedOf.poids_metal || '—'}g</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2">
                    <div className="text-xs text-gray-600 font-medium">Réel bijou</div>
                    <div className="text-lg font-bold text-gray-800">{selectedOf.poids_or_reel || '—'}g</div>
                  </div>
                  <div className={`rounded-lg p-2 ${parseFloat(selectedOf.solde_chef || 0) > 0.05 ? 'bg-red-50' : 'bg-green-50'}`}>
                    <div className={`text-xs font-medium ${parseFloat(selectedOf.solde_chef || 0) > 0.05 ? 'text-red-600' : 'text-green-600'}`}>Solde chef</div>
                    <div className={`text-lg font-bold ${parseFloat(selectedOf.solde_chef || 0) > 0.05 ? 'text-red-700' : 'text-green-700'}`}>
                      {parseFloat(selectedOf.solde_chef || 0).toFixed(3)}g
                    </div>
                  </div>
                </div>
              </div>

              {/* Notification */}
              {msg && (
                <div className={`rounded-lg px-4 py-2 text-sm flex items-center justify-between ${msg.type === 'ok' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  <span>{msg.text}</span>
                  <button onClick={() => setMsg(null)}><X className="h-3 w-3" /></button>
                </div>
              )}

              {/* Form */}
              {showForm && (
                <div className="bg-white rounded-xl border-2 border-yellow-300 p-4">
                  <h3 className="text-sm font-semibold text-gray-800 mb-3">Nouveau mouvement d'or</h3>
                  <form onSubmit={handleSubmit} className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-gray-600 font-medium">Type de mouvement</label>
                        <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value, ouvrier_id: '', motif: '' }))}
                          className="mt-1 w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-yellow-400">
                          {Object.entries(TYPE_LABELS).map(([k, v]) => (
                            <option key={k} value={k}>{v.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-gray-600 font-medium">Poids (grammes)</label>
                        <input type="number" step="0.001" min="0.001" value={form.poids_grammes}
                          onChange={e => setForm(f => ({ ...f, poids_grammes: e.target.value }))}
                          placeholder="ex: 2.500"
                          className="mt-1 w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-yellow-400" />
                      </div>
                    </div>

                    {needsOuvrier && (
                      <div>
                        <label className="text-xs text-gray-600 font-medium">Ouvrier</label>
                        <select value={form.ouvrier_id} onChange={e => setForm(f => ({ ...f, ouvrier_id: e.target.value }))}
                          className="mt-1 w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-yellow-400">
                          <option value="">-- Sélectionner --</option>
                          {ouvriers.map(o => (
                            <option key={o.id} value={o.id}>{o.nom} {o.prenom} ({o.specialite})</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {needsMotif && (
                      <div>
                        <label className="text-xs text-gray-600 font-medium text-orange-700">Justification obligatoire (supplément)</label>
                        <textarea value={form.motif} onChange={e => setForm(f => ({ ...f, motif: e.target.value }))}
                          rows={2} placeholder="Pourquoi avez-vous besoin de plus d'or ?"
                          className="mt-1 w-full text-sm border border-orange-300 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400" />
                      </div>
                    )}

                    {!needsMotif && (
                      <div>
                        <label className="text-xs text-gray-600 font-medium">Note (optionnel)</label>
                        <input type="text" value={form.motif} onChange={e => setForm(f => ({ ...f, motif: e.target.value }))}
                          placeholder="Commentaire..."
                          className="mt-1 w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-yellow-400" />
                      </div>
                    )}

                    <div className="flex gap-2 justify-end">
                      <button type="button" onClick={() => setShowForm(false)}
                        className="px-4 py-2 text-xs rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200">Annuler</button>
                      <button type="submit" disabled={saving}
                        className="px-4 py-2 text-xs rounded-lg text-white font-semibold"
                        style={{ background: '#d4af37' }}>
                        {saving ? 'Enregistrement...' : 'Enregistrer'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Bilan chefs */}
              {bilan && bilan.bilan_chefs?.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">Bilan Chef(s)</h3>
                  {bilan.bilan_chefs.map(ch => (
                    <div key={ch.chef_id} className={`rounded-lg p-3 mb-2 ${ch.alerte ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-gray-800">{ch.chef_nom || `User #${ch.chef_id}`}</span>
                        <span className={`text-sm font-bold ${ch.alerte ? 'text-red-700' : 'text-green-700'}`}>
                          {ch.alerte ? '! ' : 'OK '}Solde: {ch.solde}g
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs text-center">
                        <div><span className="text-blue-600">Reçu stock</span><br/><strong>{ch.total_recu}g</strong></div>
                        <div><span className="text-purple-600">Distribué</span><br/><strong>{ch.total_distribue}g</strong></div>
                        <div><span className="text-teal-600">Retourné</span><br/><strong>{ch.total_retourne}g</strong></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Bilan ouvriers */}
              {bilan && bilan.bilan_ouvriers?.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">Bilan Ouvrier(s)</h3>
                  {bilan.bilan_ouvriers.map(ov => (
                    <div key={ov.ouvrier_id} className={`rounded-lg p-3 mb-2 ${ov.alerte ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-gray-800">{ov.ouvrier_nom} {ov.ouvrier_prenom}</span>
                        <span className={`text-sm font-bold ${ov.alerte ? 'text-red-700' : 'text-green-700'}`}>
                          {ov.alerte ? '! ' : 'OK '}Solde: {ov.solde}g
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs text-center">
                        <div><span className="text-purple-600">Reçu du chef</span><br/><strong>{ov.total_recu}g</strong></div>
                        <div><span className="text-green-600">Rendu (bijou+limailles)</span><br/><strong>{ov.total_rendu}g</strong></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Timeline */}
              {bilan && bilan.transferts?.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">Historique mouvements</h3>
                  <div className="space-y-2">
                    {bilan.transferts.map(t => {
                      const info = TYPE_LABELS[t.type] || { label: t.type, color: 'gray', dir: 'in' };
                      return (
                        <div key={t.id} className="flex items-center gap-3 text-xs py-2 border-b border-gray-50 last:border-0">
                          <div className={`flex-shrink-0 ${info.dir === 'in' ? 'text-blue-500' : 'text-orange-500'}`}>
                            {info.dir === 'in' ? <ArrowDown className="h-4 w-4" /> : <ArrowUp className="h-4 w-4" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-800">{info.label}</div>
                            {t.ouvrier_nom && <div className="text-gray-500">→ {t.ouvrier_nom} {t.ouvrier_prenom}</div>}
                            {t.motif && <div className="text-gray-400 italic truncate">{t.motif}</div>}
                            <div className="text-gray-400">{new Date(t.date_mouvement).toLocaleString('fr-FR')}</div>
                          </div>
                          <div className="font-bold text-gray-800 flex-shrink-0">
                            {info.dir === 'in' ? '+' : '-'}{t.poids_grammes}g
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {bilan && bilan.transferts?.length === 0 && !showForm && (
                <div className="bg-white rounded-xl border border-dashed border-gray-200 p-6 text-center text-gray-400 text-sm">
                  Aucun mouvement or enregistré pour cet OF.<br/>
                  <button onClick={() => setShowForm(true)} className="mt-2 text-yellow-600 font-medium text-xs hover:underline">
                    + Enregistrer le premier mouvement
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
